import { z } from "zod";
import { idSchema } from "./text.js";
// Il resoconto di un errore del telefono del browser (scelta di Claude 24/10/2026, approvata da Lucio 23/10/2026; TEL-33):
// quando una chiamata dal browser si ferma per un errore del Voice SDK (caricamento, `Device`, chiamata, `connect` rifiutato)
// il browser lo dice al server con `POST /api/telephony/client-errors`, che scrive soltanto una riga di log. Niente audio,
// token, numeri o dati dei Lead: il messaggio tecnico passa da `phoneErrorMessageOf` (soltanto caratteri ASCII stampabili,
// cifre lunghe e gettoni mascherati, al massimo `PHONE_ERROR_MESSAGE_MAX` caratteri), sul browser e di nuovo sul server.
/**
 * In quale momento della chiamata: prima di comporre, composta e prima della risposta, in conversazione, dopo il riaggancio di
 * chi chiama. Un errore che il SDK dice dopo una chiusura da sé resta nella fase in cui la chiamata si è fermata.
 */
export const PHONE_ERROR_STAGES = ["before_dial", "ringing", "conversation", "after"];
/** L'errore come lo vede l'interfaccia (`BrowserPhoneError`). */
export const PHONE_ERROR_KINDS = ["load", "microphone", "twilio"];
/** Da dove arriva: il SDK che non si carica, il `Device`, la chiamata, `connect` che rifiuta. */
export const PHONE_ERROR_SOURCES = ["sdk_load", "device", "call", "connect"];
export const BROWSER_FAMILIES = ["chrome", "edge", "firefox", "safari", "opera", "other"];
export const OS_FAMILIES = ["macos", "windows", "ios", "android", "linux", "chromeos", "other"];
export const PHONE_ERROR_MESSAGE_MAX = 200;
/** Un anno: oltre, la pagina aperta da tanto vale «un anno». */
export const PAGE_OPEN_SECONDS_MAX = 31_536_000;
/** Il nome di un errore del SDK o del browser (`ConnectionError`, `NotAllowedError`): una parola, senza spazi. */
export const PHONE_ERROR_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_.]{0,79}$/;
const PRINTABLE_ASCII = /^[\x20-\x7e]*$/;
// Un indirizzo email diventa `[email]`. Sei cifre o più, anche separate da spazi, punti, virgole, trattini, trattini bassi,
// barre o parentesi (un numero di telefono comunque scritto), diventano `#`: il codice di Twilio (cinque cifre) e un'ora
// restano. Un gettone: un pezzo dell'alfabeto di JWT e base64 (senza punti e barre, così un indirizzo resta leggibile) di 32
// caratteri o più, oppure di 16 o più con lettere e cifre insieme; una parola lunga senza cifre («PermissionDeniedError») resta.
const LONG_DIGITS = /\+?\d(?:[ ().,_/-]*\d){5,}/g;
const EMAIL_LIKE = /[^\s@]+@[^\s@]+/g;
const TOKEN_LIKE = /[A-Za-z0-9_\-+=]{16,}/g;
const maskToken = (piece) => (piece.length >= 32 || (/\d/.test(piece) && /[A-Za-z]/.test(piece)) ? "[...]" : piece);
/**
 * Il messaggio tecnico da mandare e da scrivere nel log: i caratteri fuori dall'ASCII stampabile diventano spazi, gli spazi
 * ripetuti uno solo, ogni email diventa `[email]`, ogni sequenza di cifre lunga `#` e ogni gettone `[…]`, poi al massimo
 * `PHONE_ERROR_MESSAGE_MAX` caratteri. Riapplicato a un risultato non cambia niente. `null` se non resta niente.
 */
export function phoneErrorMessageOf(raw) {
    if (typeof raw !== "string")
        return null;
    const cleaned = raw
        .slice(0, 2_000)
        .replace(/[^\x20-\x7e]+/g, " ")
        .replace(EMAIL_LIKE, "[email]")
        .replace(TOKEN_LIKE, maskToken)
        .replace(LONG_DIGITS, "#")
        .replace(/ {2,}/g, " ")
        .trim()
        .slice(0, PHONE_ERROR_MESSAGE_MAX)
        .trim();
    return cleaned === "" ? null : cleaned;
}
/** `POST /api/telephony/client-errors`: il resoconto, `204` senza corpo. */
export const phoneErrorReportBodySchema = z.strictObject({
    kind: z.enum(PHONE_ERROR_KINDS),
    source: z.enum(PHONE_ERROR_SOURCES),
    stage: z.enum(PHONE_ERROR_STAGES),
    /** Il codice di Twilio (31000, 31005, 31401…), se c'è. */
    code: z.number().int().min(0).max(999_999).nullable(),
    /** `twilioError.name` o il nome dell'errore del browser. */
    name: z.string().regex(PHONE_ERROR_NAME_PATTERN).nullable(),
    message: z.string().max(PHONE_ERROR_MESSAGE_MAX).regex(PRINTABLE_ASCII).nullable(),
    /** La chiamata di Gioia365, se era già nata sul server. */
    callId: idSchema.nullable(),
    browser: z.enum(BROWSER_FAMILIES),
    browserMajor: z.number().int().min(0).max(9_999).nullable(),
    os: z.enum(OS_FAMILIES),
    online: z.boolean(),
    pageOpenSeconds: z.number().int().min(0).max(PAGE_OPEN_SECONDS_MAX),
});
//# sourceMappingURL=telephony-client-errors.js.map